import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';

import { createNudgeDecorationSet } from './decorations';
import { IdleDetector } from './idle-detector';
import { onIdle } from './on-idle';
import {
	createPluginState,
	createHidePromptCommand,
	initialState,
	pluginKey,
} from './plugin-factory';
import { type StandDownManager } from './stand-down-manager';

/**
 * Create the ai-quick-prompt ProseMirror plugin.
 *
 * @param options - The options
 * @param options.api - The editor plugin API
 * @param options.dispatch - The plugin dispatch function from pluginFactory
 * @param options.getIntl - Function to get intl for i18n
 * @returns A ProseMirror Plugin instance
 */
export function createAiQuickPromptPlugin({
	api,
	configItem,
	dispatch,
	getIntl,
	standDownManager,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	dispatch: Dispatch;
	getIntl: PMPluginFactoryParams['getIntl'];
	standDownManager: StandDownManager;
}): SafePlugin {
	return new SafePlugin({
		key: pluginKey,
		state: createPluginState(dispatch, initialState),

		view(view: EditorView) {
			const idleDetector = new IdleDetector({
				onIdle: () => onIdle({ api, configItem, getIntl, standDownManager, view }),
			});

			// Stop the idle detector to prevent the prompt from showing after the document is updated by AI
			const unsubscribeAIState = api?.aiExperience?.sharedState.onChange(
				({ nextSharedState, prevSharedState }) => {
					if (
						prevSharedState?.aiDecorationExperiencePluginState?.isRunning &&
						!nextSharedState?.aiDecorationExperiencePluginState?.isRunning
					) {
						idleDetector.stop();
					}
				},
			);

			return {
				update(view: EditorView, prevState: EditorState) {
					// When document changes, hide the prompt and reset idle timer
					// The plugin factory's onDocChanged callback will be called whenever there is a step,
					// which is not always a document change.
					// Selection changes are handled by pluginFactory's onSelectionChanged callback
					if (!prevState.doc.eq(view.state.doc)) {
						const pluginState = pluginKey.getState(view.state);
						if (pluginState?.isPromptVisible) {
							// Hide the prompt after a short delay to allow the editor to complete the current state update.
							// See https://hello.jira.atlassian.cloud/browse/EDITOR-3849
							setTimeout(() => {
								createHidePromptCommand()(view.state, view.dispatch);
							}, 0);
						}
						idleDetector.reset();
					}
				},
				destroy() {
					unsubscribeAIState?.();
					idleDetector.destroy();
				},
			};
		},

		props: {
			decorations(state: EditorState): DecorationSet | undefined {
				const pluginState = pluginKey.getState(state);
				if (pluginState?.isPromptVisible && pluginState.eligibleRange) {
					return createNudgeDecorationSet({
						state,
						range: pluginState.eligibleRange,
						getIntl,
					});
				}
				return undefined;
			},
		},
	});
}
